import React, { useMemo, useRef, useState } from "react";
import { Badge, Box, Flex, Grid, Spinner } from "theme-ui";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";

import { previewMaintenanceImport, commitMaintenanceImport } from "api/ServicePlans";
import { mnt, mntType, mntStatCard } from "theme/maintenanceTokens";
import { parseCsv, mapCsvColumns } from "helpers/csvParse";
import { geocodeRows, GeocodeInput } from "helpers/geocodeRows";

/**
 * MASTER_PLAN.md §3.9 work item #14 — CSV import + bulk-associate to
 * plan (mockup 04-csv-import.svg). LCG onboarding: upload a CSV,
 * preview-and-dedupe against existing Locations, associate everything
 * to the target plan in one operation.
 *
 * Flow: parse CSV -> preview (address-only, no geocode needed — dedupe
 * runs on text, not coordinates) -> user reviews/overrides decisions on
 * rows the scanner flagged -> geocode ONLY the rows that will actually
 * create a new Location (skips wasted Google API calls on rows that
 * turn out to be exact duplicates) -> commit.
 *
 * Rendered as a modal rather than the mockup's dedicated page — same
 * workflow shape, lighter to wire into the existing Plans tab. Table is
 * paginated client-side (50 rows/page) since 1,084 rows unvirtualized
 * would be sluggish, and a status filter jumps straight to the handful
 * of rows that actually need a decision.
 */

interface CsvRow {
  rowIndex: number;
  externalId?: string;
  /** Site name from the CSV's "Location Name" column. Optional: files
   * without it fall back to the street address at create time, which is
   * what every import did before Session 55. */
  name?: string;
  line1: string;
  city: string;
  state: string;
  zip?: string;
}

type RowStatus = "new" | "exact_duplicate" | "potential_duplicate" | "invalid";

interface PreviewRow extends CsvRow {
  status: RowStatus;
  matchedLocationId?: string;
  matchedLocationName?: string | null;
  matchScore?: number;
  invalidReason?: string;
}

type RowAction = "create" | "use_existing" | "skip";

const STATUS_BADGE: Record<RowStatus, { variant: string; label: string }> = {
  new: { variant: "success", label: "New" },
  exact_duplicate: { variant: "info", label: "Matches existing" },
  potential_duplicate: { variant: "warning", label: "Needs review" },
  invalid: { variant: "danger", label: "Invalid" },
};

const PAGE_SIZE = 50;

/**
 * `planId` optional (Session 62): with a plan this is the plan-page
 * import (create/match Locations, then attach each to the plan). Without
 * a plan it's the regular Locations-page import — no plan association, no
 * batch (a batch exists only once a site is added to a plan). The backend
 * decides client-vs-staff ownership from the session; the modal just
 * omits servicePlanId when there's no plan.
 */
export default function ImportSitesModal({
  planId,
  planName,
  onClose,
  onImported,
}: {
  planId?: string;
  planName?: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const hasPlan = !!planId;
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<"upload" | "reviewing" | "geocoding" | "committing" | "done">(
    "upload",
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [decisions, setDecisions] = useState<Record<number, RowAction>>({});
  const [statusFilter, setStatusFilter] = useState<RowStatus | "all">("all");
  const [page, setPage] = useState(0);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState<any>(null);

  const handleFile = async (file: File) => {
    setFileError(null);
    const text = await file.text();
    const { headers, rows: dataRows } = parseCsv(text);
    const cols = mapCsvColumns(headers);

    if (cols.line1 < 0 || cols.city < 0 || cols.state < 0) {
      const missing: string[] = [];
      if (cols.line1 < 0) missing.push("Address");
      if (cols.city < 0) missing.push("City");
      if (cols.state < 0) missing.push("State");
      const seen = headers.filter((h) => h && h.trim()).join(", ") || "(no headers detected)";
      setFileError(
        `Couldn't find these required columns: ${missing.join(", ")}. Headers detected in your file: ${seen}. Download the template below to see the exact format.`,
      );
      return;
    }

    const csvRows: CsvRow[] = dataRows
      .filter((r) => r.some((cell) => cell.trim() !== ""))
      .map((r, idx) => ({
        rowIndex: idx,
        externalId: cols.externalId >= 0 ? r[cols.externalId]?.trim() : undefined,
        name: cols.name >= 0 ? r[cols.name]?.trim() : undefined,
        line1: r[cols.line1]?.trim() || "",
        city: r[cols.city]?.trim() || "",
        state: r[cols.state]?.trim() || "",
        zip: cols.zip >= 0 ? r[cols.zip]?.trim() : undefined,
      }));

    if (!csvRows.length) {
      setFileError("No data rows found in this file.");
      return;
    }

    const previewResults = await previewMaintenanceImport({ data: { rows: csvRows } });
    if (!Array.isArray(previewResults)) {
      setFileError("Couldn't preview this file — try again.");
      return;
    }

    const byIndex = new Map(previewResults.map((p: any) => [p.rowIndex, p]));
    const merged: PreviewRow[] = csvRows.map((r) => {
      const p = byIndex.get(r.rowIndex) || { status: "new" };
      return { ...r, ...p };
    });

    const initialDecisions: Record<number, RowAction> = {};
    merged.forEach((r) => {
      if (r.status === "invalid") initialDecisions[r.rowIndex] = "skip";
      else if (r.status === "new") initialDecisions[r.rowIndex] = "create";
      else initialDecisions[r.rowIndex] = "use_existing"; // exact + potential dupes default safe
    });

    setRows(merged);
    setDecisions(initialDecisions);
    setStep("reviewing");
    setPage(0);
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  // v3.4 UX pass — downloadable template CSV. First-time users
  // wrestling with column names is real friction; a concrete example
  // beats "figure out what headers to use." Kompan / Goddard-shape
  // clients handing us a spreadsheet won't necessarily label their
  // columns the way our parser expects; the template shows the
  // canonical names and a couple example rows.
  const downloadTemplate = () => {
    const csv = [
      "External ID,Location Name,Address,City,State,Zip",
      "LCG-001,Example Daycare A,123 Main St,Orlando,FL,32801",
      "LCG-002,Example Daycare B,456 Oak Ave,Tampa,FL,33602",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "maintenance-locations-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const setDecision = (rowIndex: number, action: RowAction) => {
    setDecisions((prev) => ({ ...prev, [rowIndex]: action }));
  };

  const counts = useMemo(() => {
    const c: Record<RowStatus, number> = {
      new: 0,
      exact_duplicate: 0,
      potential_duplicate: 0,
      invalid: 0,
    };
    rows.forEach((r) => c[r.status]++);
    return c;
  }, [rows]);

  const filteredRows = useMemo(
    () => (statusFilter === "all" ? rows : rows.filter((r) => r.status === statusFilter)),
    [rows, statusFilter],
  );

  // GAP-24 — what actually goes in, and what is being left behind.
  const toImportCount = useMemo(
    () => rows.filter((r) => decisions[r.rowIndex] !== "skip").length,
    [rows, decisions],
  );
  const heldBack = rows.length - toImportCount;
  const pageRows = filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const submitImport = async () => {
    const toCreate = rows.filter((r) => decisions[r.rowIndex] === "create");

    let geocoded: Record<number, { latitude: number | null; longitude: number | null }> = {};

    if (toCreate.length) {
      setStep("geocoding");
      setGeocodeProgress({ done: 0, total: toCreate.length });

      const inputs: GeocodeInput[] = toCreate.map((r) => ({
        rowIndex: r.rowIndex,
        line1: r.line1,
        city: r.city,
        state: r.state,
        zip: r.zip,
      }));

      const geoResults = await geocodeRows(inputs, (done, total) =>
        setGeocodeProgress({ done, total }),
      );
      geocoded = Object.fromEntries(
        geoResults.map((g) => [g.rowIndex, { latitude: g.latitude, longitude: g.longitude }]),
      );

      const failedCount = geoResults.filter((g) => g.latitude == null).length;
      if (failedCount) {
        toast.add(
          `${failedCount} row${failedCount === 1 ? "" : "s"} couldn't be geocoded and will be skipped — they can be re-imported after fixing the address.`,
          "var(--theme-ui-colors-warning)",
        );
      }
    }

    setStep("committing");

    const commitRows = rows
      .filter((r) => decisions[r.rowIndex] !== "skip")
      .map((r) => {
        const action = decisions[r.rowIndex];
        if (action === "use_existing") {
          return {
            rowIndex: r.rowIndex,
            action: "use_existing" as const,
            existingLocationId: r.matchedLocationId,
          };
        }
        const geo = geocoded[r.rowIndex];
        if (!geo || geo.latitude == null || geo.longitude == null) {
          return { rowIndex: r.rowIndex, action: "skip" as const };
        }
        return {
          rowIndex: r.rowIndex,
          action: "create" as const,
          externalId: r.externalId,
          name: r.name,
          line1: r.line1,
          city: r.city,
          state: r.state,
          zip: r.zip,
          latitude: geo.latitude,
          longitude: geo.longitude,
        };
      });

    const res = await commitMaintenanceImport({
      data: {
        // Plan import sends servicePlanId; Locations-page import omits it
        // (no plan association, no batch).
        ...(planId ? { servicePlanId: planId } : {}),
        rows: commitRows,
      },
    });

    setStep("done");

    if (res?.added != null || res?.lcpsResolved != null) {
      setSummary(res);
      onImported();
    } else {
      toast.add(res?.message || "Error importing Locations.", "var(--theme-ui-colors-danger)");
      setStep("reviewing");
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>
        {hasPlan ? `Import Locations — ${planName}` : "Import Locations"}
      </ModalHeader>
      <ModalBody>
        {step === "upload" && (
          <Box>
            <P marginTop={0} color={themevals.colors.dkgrey}>
              Upload a CSV with Address, City, State, and Zip columns (School ID or similar
              identifier is optional). Rows are checked against existing Locations before anything
              is created.
            </P>
            <Flex sx={{ gap: "10px", alignItems: "center", marginTop: "12px", flexWrap: "wrap" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
              />
              <BasicButton type="button" outline scheme="secondary" onClick={downloadTemplate}>
                Download template
              </BasicButton>
            </Flex>
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              margin={0}
              marginTop={"10px"}
              color={themevals.colors.dkgrey}
            >
              Column names are case-insensitive. Common variations like &quot;Street Address&quot;, &quot;Zip Code&quot;,
              or &quot;Location Name&quot; are recognized automatically.
            </Subtext>
            {fileError && (
              <P fontSize={"13px"} color={themevals.colors.danger} marginTop={"12px"}>
                {fileError}
              </P>
            )}
          </Box>
        )}

        {step === "reviewing" && (
          <Box>
            {/* GAP-21 (mockup 04) — the summary the import was missing.
             * At 1,084 rows the first question is not "what is in row 1",
             * it is "how much of this is new, how much already exists,
             * and how much needs me". Each card is also the filter for
             * what it counts. */}
            <Grid columns={[2, 4]} gap={"12px"} sx={{ marginBottom: "14px" }}>
              <ImportStatCard label="Total rows" value={rows.length} />
              <ImportStatCard
                label="New Locations"
                value={counts.new}
                sub="will be created"
                valueColor={mnt.green}
                active={statusFilter === "new"}
                onClick={() => {
                  setStatusFilter(statusFilter === "new" ? "all" : "new");
                  setPage(0);
                }}
              />
              <ImportStatCard
                label="Match existing"
                value={counts.exact_duplicate}
                sub="reuse in place"
                valueColor={mnt.link}
                active={statusFilter === "exact_duplicate"}
                onClick={() => {
                  setStatusFilter(statusFilter === "exact_duplicate" ? "all" : "exact_duplicate");
                  setPage(0);
                }}
              />
              <ImportStatCard
                label="Needs review"
                value={counts.potential_duplicate + counts.invalid}
                sub={
                  counts.invalid > 0
                    ? counts.potential_duplicate + " possible dupes \u00b7 " + counts.invalid + " invalid"
                    : "potential duplicates"
                }
                valueColor={
                  counts.potential_duplicate + counts.invalid > 0 ? mnt.amberDeep : undefined
                }
                active={statusFilter === "potential_duplicate"}
                onClick={() => {
                  setStatusFilter(
                    statusFilter === "potential_duplicate" ? "all" : "potential_duplicate",
                  );
                  setPage(0);
                }}
              />
            </Grid>

            {/* The cards above ARE the filter, so the old badge chip row
             * was saying the same thing twice. Only the escape hatch
             * survives. */}
            {statusFilter !== "all" && (
              <Flex sx={{ gap: "8px", alignItems: "center", marginBottom: "14px" }}>
                <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                  Filtered to {STATUS_BADGE[statusFilter as RowStatus].label.toLowerCase()} rows
                </Subtext>
                <Box
                  as="button"
                  onClick={() => {
                    setStatusFilter("all");
                    setPage(0);
                  }}
                  sx={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    fontFamily: "Poppins",
                    fontSize: "11px",
                    color: mnt.link,
                    cursor: "pointer",
                  }}
                >
                  Show all rows
                </Box>
              </Flex>
            )}

            <Box sx={{ maxHeight: "420px", overflowY: "auto", border: "1px solid #E4E2D8", borderRadius: "8px" }}>
              <Grid
                columns={"1fr 100px 1.4fr 1fr"}
                gap={0}
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#F1EFE8",
                  padding: "8px 12px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: themevals.colors.dkgrey,
                }}
              >
                <span>Site</span>
                <span>Status</span>
                <span>Match</span>
                <span>Decision</span>
              </Grid>
              {pageRows.map((r) => (
                <Grid
                  key={r.rowIndex}
                  columns={"1fr 100px 1.4fr 1fr"}
                  gap={0}
                  sx={{
                    padding: "8px 12px",
                    fontSize: "13px",
                    borderTop: "1px solid #F1EFE8",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {r.name ? (
                      <>
                        <Box sx={{ fontWeight: 600 }}>{r.name}</Box>
                        <Box sx={{ fontSize: "11px", color: mnt.textMuted }}>
                          {r.line1 || "-"}, {r.city}, {r.state} {r.zip || ""}
                        </Box>
                      </>
                    ) : (
                      <>
                        {r.line1 || "-"}, {r.city}, {r.state} {r.zip || ""}
                      </>
                    )}
                  </span>
                  <span>
                    <Badge variant={STATUS_BADGE[r.status].variant as any}>
                      {STATUS_BADGE[r.status].label}
                    </Badge>
                  </span>
                  <span style={{ color: themevals.colors.dkgrey }}>
                    {r.status === "invalid"
                      ? r.invalidReason
                      : r.matchedLocationName || (r.status === "new" ? "—" : r.matchedLocationId)}
                  </span>
                  <span>
                    {r.status === "invalid" ? (
                      <Span fontSize={"12px"} color={themevals.colors.dkgrey}>
                        Skipped
                      </Span>
                    ) : (
                      <select
                        value={decisions[r.rowIndex] || "create"}
                        onChange={(e) => setDecision(r.rowIndex, e.target.value as RowAction)}
                        style={{ fontSize: "12px", padding: "4px 6px" }}
                      >
                        <option value="create">Create new Location</option>
                        {r.status !== "new" && (
                          <option value="use_existing">Use existing Location</option>
                        )}
                        <option value="skip">Skip this row</option>
                      </select>
                    )}
                  </span>
                </Grid>
              ))}
            </Box>

            <Flex sx={{ justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
              <Subtext fontSize={"12px"} marginTop={0} marginBottom={0}>
                {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
                {statusFilter !== "all" ? ` (filtered)` : ""} · Page {page + 1} of {totalPages}
              </Subtext>
              <Flex sx={{ gap: "8px" }}>
                <BasicButton
                  type="button"
                  scheme="infoOutline"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Prev
                </BasicButton>
                <BasicButton
                  type="button"
                  scheme="infoOutline"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  Next
                </BasicButton>
              </Flex>
            </Flex>
          </Box>
        )}

        {(step === "geocoding" || step === "committing") && (
          <Box sx={{ textAlign: "center", paddingY: "2rem" }}>
            <Spinner />
            <P marginTop={"12px"} color={themevals.colors.dkgrey}>
              {step === "geocoding"
                ? `Geocoding new Locations — ${geocodeProgress.done} of ${geocodeProgress.total}`
                : hasPlan
                  ? "Creating Locations and adding them to the plan…"
                  : "Creating Locations…"}
            </P>
          </Box>
        )}

        {step === "done" && summary && (
          <Box>
            <H5 fontFamily="Poppins" fontWeight={600}>
              {summary.attachFailed ? "Import finished with a problem" : "Import complete"}
            </H5>
            <P fontSize={"13px"}>
              {summary.locationsCreated} Location{summary.locationsCreated === 1 ? "" : "s"} created ·{" "}
              {summary.locationsMatchedExisting} matched to existing Locations
              {hasPlan
                ? ` · ${summary.added} added to plan${
                    summary.alreadyPresent ? ` (${summary.alreadyPresent} already on plan)` : ""
                  }`
                : ""}{" "}
              · {summary.skipped} skipped
            </P>
            {/* KNOWN_ISSUES #21 — the plan attach can fail on its own now
             * rather than taking the request down, so it has to be SAID.
             * The Endeavor Schools import lost 98 plan associations while
             * every Location was created, and the old UI had no way to
             * express that: the sites appeared, so it read as success.
             * Names the exact recovery, because re-running the same file
             * is safe and is the fix. */}
            {summary.attachFailed && (
              <Box
                sx={{
                  marginTop: "12px",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  background: "#FFF4F4",
                  border: `1px solid ${themevals.colors.danger}`,
                }}
              >
                <P fontSize={"13px"} color={themevals.colors.danger} marginTop={0} marginBottom={"6px"} fontWeight={600}>
                  {summary.attachRetryableLcpIds?.length ?? 0} Location
                  {(summary.attachRetryableLcpIds?.length ?? 0) === 1 ? "" : "s"} could not be added to
                  the plan.
                </P>
                <P fontSize={"12px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"6px"}>
                  Every Location above was created and is tied to the right client, so nothing was
                  lost. They are just not on the plan yet. Import the same file again to finish the
                  job: Locations that already exist will be matched rather than duplicated, and only
                  the missing plan associations will be written.
                </P>
                {summary.attachError && (
                  <P fontSize={"11px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={0}>
                    Reported cause: {summary.attachError}
                  </P>
                )}
              </Box>
            )}
            {summary.errors?.length > 0 && (
              <Box sx={{ marginTop: "10px" }}>
                <P fontSize={"13px"} color={themevals.colors.danger} marginBottom={"6px"}>
                  {summary.errors.length} row{summary.errors.length === 1 ? "" : "s"} failed:
                </P>
                <Box sx={{ maxHeight: "160px", overflowY: "auto" }}>
                  {summary.errors.map((e: any, i: number) => (
                    <P key={i} fontSize={"12px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"4px"}>
                      Row {e.rowIndex + 1}: {e.reason}
                    </P>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        {step === "reviewing" && (
          <Flex sx={{ alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {/* GAP-24 (mockup 04) — say what is being held back, next to
             * the button that acts. "Import 1,070" alone leaves you
             * wondering what happened to the other 14. */}
            {heldBack > 0 && (
              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
                {heldBack} row{heldBack === 1 ? "" : "s"} held back until resolved
              </Subtext>
            )}
            <BasicButton type="button" onClick={submitImport}>
              Import {toImportCount} Location{toImportCount === 1 ? "" : "s"}
            </BasicButton>
          </Flex>
        )}
        {step === "done" ? (
          <BasicButton type="button" onClick={onClose}>
            Done
          </BasicButton>
        ) : (
          step !== "geocoding" &&
          step !== "committing" && (
            <BasicButton type="button" scheme="infoOutline" onClick={onClose}>
              Cancel
            </BasicButton>
          )
        )}
      </ModalFooter>
    </Modal>
  );
}

/**
 * Import summary card (mockup 04). Beige and calm like every other stat
 * card in the module; the count carries the color, and the card is the
 * filter for what it counts. `onClick` omitted (Total rows) renders it
 * inert rather than looking clickable and doing nothing.
 */
function ImportStatCard({
  label,
  value,
  sub,
  valueColor,
  active,
  onClick,
}: {
  label: string;
  value: number;
  sub?: string;
  valueColor?: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        ...mntStatCard(),
        padding: "12px 14px",
        cursor: onClick ? "pointer" : "default",
        outline: active ? "1px solid " + mnt.ink : "none",
      }}
    >
      <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>{label}</Box>
      <Box sx={{ ...mntType.valueLg, marginTop: "4px", color: valueColor || mnt.ink }}>
        {value.toLocaleString()}
      </Box>
      <Box sx={{ ...mntType.labelSm, marginTop: "2px" }}>{sub || "\u00A0"}</Box>
    </Box>
  );
}
